import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Settings, User, Bell, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function SettingsPage() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-4">Account Settings</h1>
        <p className="text-lg text-gray-700 mb-6">
          Manage your account preferences and settings to customize your experience with AISEOBlogGenerator.
        </p>
      </div>

      <div className="prose max-w-none">
        <h2>Account Management</h2>
        <p>
          Your account settings allow you to customize your experience, manage your profile information, and control various aspects of how the application works for you.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <User className="w-5 h-5 text-blue-700" />
            </div>
            <h3 className="text-lg font-semibold">Profile Settings</h3>
          </div>
          
          <p className="text-gray-700 mb-4">
            Manage your personal information and account details.
          </p>
          
          <div className="space-y-4">
            <div className="p-3 border rounded-md">
              <h4 className="font-medium mb-1">Name & Email</h4>
              <p className="text-sm text-gray-600">
                Update your display name and email address through your Clerk account profile.
              </p>
              <div className="mt-2">
                <Button variant="outline" size="sm" disabled>View Profile</Button>
              </div>
            </div>
            
            <div className="p-3 border rounded-md">
              <h4 className="font-medium mb-1">Password & Security</h4>
              <p className="text-sm text-gray-600">
                Manage your password, enable two-factor authentication, and review active sessions.
              </p>
              <div className="mt-2">
                <Button variant="outline" size="sm" disabled>Security Settings</Button>
              </div>
            </div>
            
            <div className="p-3 border rounded-md">
              <h4 className="font-medium mb-1">Delete Account</h4>
              <p className="text-sm text-gray-600">
                Permanently remove your account and all associated data from our systems.
              </p>
              <div className="mt-2">
                <Button variant="outline" size="sm" className="text-red-500 border-red-200 hover:bg-red-50" disabled>
                  Delete Account
                </Button>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white border rounded-lg p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Settings className="w-5 h-5 text-blue-700" />
            </div>
            <h3 className="text-lg font-semibold">Application Preferences</h3>
          </div>
          
          <p className="text-gray-700 mb-4">
            Customize how AISEOBlogGenerator works for you.
          </p>
          
          <div className="space-y-4">
            <div className="p-3 border rounded-md">
              <h4 className="font-medium mb-1">Default Content Settings</h4>
              <p className="text-sm text-gray-600">
                Set your preferred word count, tone, and other default options for the content generator.
              </p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div>
                  <span className="text-xs text-gray-500">Default Word Count</span>
                  <div className="border rounded p-1 mt-1 text-sm bg-gray-50">1,000 words</div>
                </div>
                <div>
                  <span className="text-xs text-gray-500">Default Tone</span>
                  <div className="border rounded p-1 mt-1 text-sm bg-gray-50">Professional</div>
                </div>
              </div>
            </div>
            
            <div className="p-3 border rounded-md">
              <h4 className="font-medium mb-1">Marketer Mode</h4>
              <p className="text-sm text-gray-600">
                Toggle whether Marketer Mode is enabled by default when you open the content writer.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <div className="w-10 h-5 bg-gray-200 rounded-full relative">
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                </div>
                <span className="text-sm text-gray-600">Disabled by default</span>
              </div>
            </div>
            
            <div className="p-3 border rounded-md">
              <h4 className="font-medium mb-1">Writing Styles Panel</h4>
              <p className="text-sm text-gray-600">
                Control whether the writing styles panel appears by default when you open the content writer.
              </p>
              <div className="mt-3 flex items-center gap-2">
                <div className="w-10 h-5 bg-blue-600 rounded-full relative">
                  <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                </div>
                <span className="text-sm text-gray-600">Enabled by default</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border rounded-lg p-6 mt-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Bell className="w-5 h-5 text-blue-700" />
          </div>
          <h2 className="text-xl font-bold">Notifications & Communications</h2>
        </div>
        
        <div className="space-y-6">
          <div>
            <h3 className="font-medium text-lg mb-3">Email Notifications</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border rounded-md">
                <div>
                  <h4 className="font-medium">Product Updates</h4>
                  <p className="text-sm text-gray-600">Receive emails about new features and improvements</p>
                </div>
                <div className="w-10 h-5 bg-blue-600 rounded-full relative">
                  <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded-md">
                <div>
                  <h4 className="font-medium">Tips & Tutorials</h4>
                  <p className="text-sm text-gray-600">Helpful guides to get the most out of the platform</p>
                </div>
                <div className="w-10 h-5 bg-blue-600 rounded-full relative">
                  <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between p-3 border rounded-md">
                <div>
                  <h4 className="font-medium">Marketing Communications</h4>
                  <p className="text-sm text-gray-600">Offers, promotions, and news from our team</p>
                </div>
                <div className="w-10 h-5 bg-gray-200 rounded-full relative">
                  <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 border rounded-lg p-6 mt-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-2 rounded-lg">
            <Shield className="w-5 h-5 text-blue-700" />
          </div>
          <h2 className="text-xl font-bold">Privacy Settings</h2>
        </div>
        
        <p className="text-gray-700 mb-6">
          Control how your data is used and what information is collected while you use the application.
        </p>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between p-3 border rounded-md bg-white">
            <div>
              <h4 className="font-medium">Analytics Cookies</h4>
              <p className="text-sm text-gray-600">Allow us to collect anonymous usage data to improve the service</p>
            </div>
            <div className="w-10 h-5 bg-blue-600 rounded-full relative">
              <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
            </div>
          </div>
          
          <div className="flex items-center justify-between p-3 border rounded-md bg-white">
            <div>
              <h4 className="font-medium">Content Improvement</h4>
              <p className="text-sm text-gray-600">Allow us to use your generated content to improve our AI models</p>
            </div>
            <div className="w-10 h-5 bg-gray-200 rounded-full relative">
              <div className="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full"></div>
            </div>
          </div>
          
          <div className="mt-4">
            <Button variant="outline" size="sm" asChild>
              <Link to="/cookie-settings">Manage Cookie Settings</Link>
            </Button>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8 pt-6 border-t">
        <Button variant="outline" asChild>
          <Link to="/docs/strike-list">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back: Strike List
          </Link>
        </Button>
        <Button asChild>
          <Link to="/docs/plans">
            Next: Subscription Plans
            <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}